import { useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { money, uploadImage, deleteImage, cdnKey } from '../../api/plannerApi';
import { isPayable, STATUSES, TAG_PALETTE } from '../../types/board';
import type { Attachment, Board, Item } from '../../types/board';

const inputCls = 'w-full border border-line bg-cream px-2.5 py-2 text-sm outline-none focus:border-terra';
const labelCls = 'text-xs font-bold uppercase tracking-wider text-muted mb-1.5';

export default function ItemEditDrawer({
  item,
  board,
  editToken,
  onUpdate,
  onDelete,
  onToggleCategory,
  onAddPayment,
  onRemovePayment,
  onAddAttachment,
  onRemoveAttachment,
  onClose,
}: {
  item: Item;
  board: Board;
  editToken: string;
  onUpdate: (patch: Partial<Item>) => void;
  onDelete: () => void;
  onToggleCategory: (categoryId: string) => void;
  onAddPayment: (p: { date: string; amount: number; note: string }) => void;
  onRemovePayment: (id: string) => void;
  onAddAttachment: (url: string) => void;
  onRemoveAttachment: (id: string) => void;
  onClose: () => void;
}) {
  const [payDate, setPayDate] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [attUrl, setAttUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Attachments uploaded via the CDN are images (thumbnail grid); anything
  // else is a plain link row.
  const images = item.attachments.filter((a) => cdnKey(a.url));
  const links = item.attachments.filter((a) => !cdnKey(a.url));

  const onFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    setUploadError(null);
    try {
      for (const file of files) {
        const result = await uploadImage(file);
        onAddAttachment(result.url);
      }
    } catch {
      setUploadError('Upload failed — check the image and try again.');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async (a: Attachment) => {
    onRemoveAttachment(a.id);
    const key = cdnKey(a.url);
    if (!key) return;
    try {
      await deleteImage(key, editToken);
    } catch {
      // Board already saved without the image; worker deletion is best-effort.
    }
  };

  const paid = (item.payments || []).reduce((a, p) => a + (p.amount || 0), 0);
  const exp = item.expectedPrice || 0;
  const pct = exp > 0 ? Math.min(100, Math.round((paid / exp) * 100)) : 0;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (previewUrl) setPreviewUrl(null);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, previewUrl]);

  return (
    <div className="fixed inset-0 z-[90] bg-[oklch(24%_0.02_50_/_0.35)]" onClick={onClose}>
      <div
        className="fixed top-0 right-0 bottom-0 w-[420px] max-w-[92vw] bg-card z-[91] shadow-[-8px_0_30px_oklch(24%_0.02_50_/_0.15)] overflow-y-auto px-6.5 pt-7 pb-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4.5 gap-3">
          <input
            value={item.title}
            onChange={(e) => onUpdate({ title: e.target.value })}
            className="font-serif italic text-[22px] bg-transparent border-none border-b border-dashed border-line outline-none w-[85%] pb-1 focus:border-terra"
          />
          <button onClick={onClose} className="text-xl text-muted leading-none">
            ×
          </button>
        </div>

        <label className="flex items-center gap-2 text-sm text-muted mb-5">
          <input
            type="checkbox"
            checked={item.priority}
            onChange={(e) => onUpdate({ priority: e.target.checked })}
            className="accent-terra w-4 h-4"
          />
          Priority item
        </label>

        <div className={labelCls}>Status</div>
        <select
          value={item.status}
          onChange={(e) => onUpdate({ status: e.target.value as Item['status'] })}
          className={`${inputCls} mb-5`}
        >
          {STATUSES.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>

        <div className={labelCls}>Categories</div>
        <div className="flex flex-wrap gap-1.5 mb-5">
          {board.categories.map((c, idx) => {
            const on = item.categoryIds.includes(c.id);
            const t = TAG_PALETTE[idx % TAG_PALETTE.length];
            return (
              <button
                key={c.id}
                onClick={() => onToggleCategory(c.id)}
                className="rounded-md px-3 py-1.5 text-xs font-semibold"
                style={{
                  border: `1.5px solid ${on ? t.text : 'oklch(88% 0.015 60)'}`,
                  background: on ? t.bg : 'transparent',
                  color: on ? t.text : 'oklch(45% 0.02 50)',
                }}
              >
                {c.name}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div>
            <div className={labelCls}>Vendor</div>
            <input
              value={item.vendor}
              onChange={(e) => onUpdate({ vendor: e.target.value })}
              placeholder="e.g. Studio Bunga"
              className={inputCls}
            />
          </div>
          <div>
            <div className={labelCls}>Due date</div>
            <input
              type="date"
              value={item.dueDate}
              onChange={(e) => onUpdate({ dueDate: e.target.value })}
              className={inputCls}
            />
          </div>
        </div>

        <div className={labelCls}>Expected price (Rp)</div>
        <input
          type="number"
          value={item.expectedPrice}
          onChange={(e) => onUpdate({ expectedPrice: Number(e.target.value) || 0 })}
          className={`${inputCls} mb-5`}
        />

        {isPayable(item.status) ? (
          <>
            <div className={labelCls}>
              Payments — {money(paid)} of {money(exp)}
            </div>
            <div className="h-1.5 rounded-full bg-[oklch(92%_0.012_60)] overflow-hidden mb-3">
              <div className="h-full rounded-full bg-sage" style={{ width: `${pct}%` }} />
            </div>

            <div className="flex flex-col gap-1.5 mb-2.5">
              {item.payments.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between text-xs px-2 py-1.5 bg-cream border border-line"
                >
                  <span className="text-muted">{p.date}</span>
                  <span className="flex-1 px-2">{p.note}</span>
                  <span className="tabular-nums">{money(p.amount)}</span>
                  <button onClick={() => onRemovePayment(p.id)} className="text-terra ml-2">
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-[1fr_1fr_auto] gap-1.5 mb-6">
              <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="border border-line px-2 py-1.5 text-xs bg-cream" />
              <input
                type="number"
                placeholder="Amount"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                className="border border-line px-2 py-1.5 text-xs bg-cream"
              />
              <button
                onClick={() => {
                  const amount = Number(payAmount) || 0;
                  if (!amount) return;
                  onAddPayment({ date: payDate || new Date().toISOString().slice(0, 10), amount, note: '' });
                  setPayDate('');
                  setPayAmount('');
                }}
                className="rounded-md bg-sage text-white px-3 py-1.5 text-xs"
              >
                Add
              </button>
            </div>
          </>
        ) : (
          <div className="text-xs text-muted mb-6">Payments unlock once this item is booked.</div>
        )}

        <div className={labelCls}>Attachments</div>
        {images.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2.5">
            {images.map((a) => (
              <div key={a.id} className="relative">
                <button
                  onClick={() => setPreviewUrl(a.url)}
                  className="p-0 border-none cursor-zoom-in"
                  aria-label="Preview image"
                >
                  <img
                    src={a.url}
                    alt=""
                    className="w-20 h-20 object-cover rounded-md border border-line"
                  />
                </button>
                <button
                  onClick={() => removeImage(a)}
                  aria-label="Remove image"
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-terra text-white text-[11px] leading-none flex items-center justify-center hover:bg-ink"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        {previewUrl && (
          <div
            className="fixed inset-0 z-[99] bg-[oklch(24%_0.02_50_/_0.8)] flex items-center justify-center p-6"
            onClick={() => setPreviewUrl(null)}
          >
            <img
              src={previewUrl}
              alt="Preview"
              className="max-w-full max-h-[90vh] object-contain rounded-md bg-ink"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setPreviewUrl(null)}
              aria-label="Close preview"
              className="absolute top-5 right-5 text-cream text-3xl leading-none"
            >
              ×
            </button>
          </div>
        )}
        <label className="inline-flex items-center justify-center gap-2 rounded-md border-[1.5px] border-dashed border-line text-xs text-muted px-3 py-2 cursor-pointer mb-2.5 hover:border-terra hover:text-terra">
          {uploading ? 'Uploading…' : '⬆ Upload images'}
          <input
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={onFiles}
            disabled={uploading}
          />
        </label>
        {uploadError && <div className="text-xs text-terra mb-2">{uploadError}</div>}
        <div className="flex flex-col gap-1.5 mb-2">
          {links.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between text-xs px-2 py-1.5 bg-cream border border-line"
            >
              <a href={a.url} target="_blank" rel="noreferrer" className="flex-1 min-w-0 truncate">
                {a.url}
              </a>
              <button onClick={() => onRemoveAttachment(a.id)} className="text-terra ml-2">
                ×
              </button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-[1fr_auto] gap-1.5 mb-5">
          <input
            value={attUrl}
            onChange={(e) => setAttUrl(e.target.value)}
            placeholder="https://… (contract, inspo, link)"
            className="border border-line px-2 py-1.5 text-xs bg-cream"
          />
          <button
            onClick={() => {
              if (!attUrl.trim()) return;
              onAddAttachment(attUrl.trim());
              setAttUrl('');
            }}
            className="rounded-md bg-terra text-white px-3 py-1.5 text-xs"
          >
            Add
          </button>
        </div>

        <div className={labelCls}>Notes</div>
        <textarea
          value={item.notes}
          onChange={(e) => onUpdate({ notes: e.target.value })}
          rows={3}
          className={`${inputCls} mb-6.5 resize-y`}
        />

        <button
          onClick={() => {
            if (confirm('Delete this item?')) onDelete();
          }}
          className="w-full rounded-lg border-[1.5px] border-[oklch(70%_0.09_20)] text-terra py-2.5 text-sm hover:bg-terra hover:text-white"
        >
          Delete item
        </button>
      </div>
    </div>
  );
}
